/**
 * Conflict Service
 * API calls for conflict management
 */

import BaseApiService from '../base/BaseApiService';
import {
  Conflict,
  ConflictFilters,
  ConflictExportFilters,
  ConflictExportResponse,
  WorklistResponse,
  ConflictStats,
  ResolveConflictData,
  MissingRoleField
} from '@app-types/conflict';

class ConflictService extends BaseApiService {
  /**
   * Get worklist of conflicts for the current user
   */
  async getWorklist(filters: ConflictFilters = {}): Promise<WorklistResponse> {
    const params = this.buildQueryParams(filters);
    return this.get<WorklistResponse>(`/conflicts/worklist?${params}`);
  }

  /**
   * Get every conflict matching the worklist filters, ignoring pagination, used for
   * full-dataset export.
   */
  async getWorklistForExport(filters: ConflictExportFilters = {}): Promise<ConflictExportResponse> {
    const params = this.buildQueryParams(filters);
    return this.get<ConflictExportResponse>(`/conflicts/worklist/export?${params}`);
  }

  /**
   * Get conflict statistics
   */
  async getStats(entityType?: string): Promise<ConflictStats> {
    const params = entityType ? `?entityType=${entityType}` : '';
    return this.get<ConflictStats>(`/conflicts/stats${params}`);
  }

  /**
   * Get a single conflict by ID
   */
  async getById(id: number): Promise<Conflict> {
    return this.get<Conflict>(`/conflicts/${id}`);
  }

  /**
   * Get conflicts for a specific entity
   */
  async getByEntity(entityType: string, entityId: number, includeResolved = false): Promise<Conflict[]> {
    const params = includeResolved ? '?includeResolved=true' : '';
    return this.get<Conflict[]>(`/conflicts/entity/${entityType}/${entityId}${params}`);
  }

  /**
   * Check if entity has open conflict of a specific type
   */
  async checkConflict(entityType: string, entityId: number, conflictTypeCode: string): Promise<boolean> {
    const response = await this.get<{ hasConflict: boolean }>(
      `/conflicts/check/${entityType}/${entityId}/${conflictTypeCode}`
    );
    return response.hasConflict;
  }

  /**
   * Acknowledge a conflict
   */
  async acknowledge(id: number): Promise<Conflict> {
    return this.post<Conflict>(`/conflicts/${id}/acknowledge`);
  }

  /**
   * Resolve a conflict
   */
  async resolve(id: number, data: ResolveConflictData): Promise<Conflict> {
    return this.post<Conflict>(`/conflicts/${id}/resolve`, data);
  }

  /**
   * "Role not needed": drop one contact and field from a Missing Contract Role conflict (Admin only)
   */
  async dismissMissingContractRole(id: number, contactId: number, field: MissingRoleField): Promise<void> {
    await this.post<{ success: boolean }>(`/conflicts/${id}/missing-contract-role/dismiss`, { contactId, field });
  }

  /**
   * Manually trigger division conflict detection
   */
  async detectDivisionConflict(
    contractId: number
  ): Promise<{ detected: boolean; conflict?: Conflict; message?: string }> {
    return this.post<{ detected: boolean; conflict?: Conflict; message?: string }>(
      `/conflicts/detect/division/${contractId}`
    );
  }
}

const conflictService = new ConflictService();
export default conflictService;
